import React, { useState, useEffect } from "react";
import "./App.css";

// Gender mapping: 1 → 男, 2 → 女, 3 → 不明
const GenderLabel = {
  1: "男",
  2: "女",
  3: "不明",
};

// issuerMetadata に定義が無いフィールドや、キーが揺れるフィールドをここで補完
const FieldLabelMap = {
  birthday: "生年月日",
  issuanceDate: "発行日",
  expirationDate: "有効期限",
};

// Helper to convert credential values to human‑readable labels
function formatCredentialValue(key, value) {
  if (key === "gender") {
    const label = GenderLabel[Number(value)];
    if (label) return label;
  }
  return typeof value === "object" ? JSON.stringify(value) : value;
}

function isValidUrl() {
  const params = getQueryParams();
  return params.data !== undefined;
}

function getQueryParams() {
  const rawQuery = window.location.search.split("?")[1] || "";
  const params = Object.fromEntries(
    rawQuery.split("&").map((param) => {
      const [key, value] = param.split("=");
      return [decodeURIComponent(key), decodeURIComponent(value)];
    })
  );
  return params;
}

function base64urlDecode(str) {
  // Convert base64url to base64 by replacing - with + and _ with /
  str = str.replace(/-/g, "+").replace(/_/g, "/");

  // Add padding if needed
  while (str.length % 4) {
    str += "=";
  }

  try {
    // Decode base64 to binary string
    const binaryStr = window.atob(str);
    // Convert binary string to UTF-8 array
    const bytes = new Uint8Array(binaryStr.length);
    for (let i = 0; i < binaryStr.length; i++) {
      bytes[i] = binaryStr.charCodeAt(i);
    }
    // Convert UTF-8 array to string
    return new TextDecoder().decode(bytes);
  } catch (e) {
    console.error("Error decoding base64url:", e);
    return "";
  }
}

function App() {
  const [verificationResult, setVerificationResult] = useState("検証中...");
  const [vcData, setVcData] = useState(null);
  const [responseData, setResponseData] = useState(null); // Store full response data
  const [isVerifying, setIsVerifying] = useState(true);
  const [isValidUrlFormat, setIsValidUrlFormat] = useState(false);

  // Add new state variables for the verification UI
  const [verificationStatus, setVerificationStatus] = useState("pending"); // pending, success, warning, error
  const [verificationItems, setVerificationItems] = useState([
    {
      id: 1,
      title: "デジタル署名の検証",
      status: "pending",
      description: "検証待機中...",
    },
    {
      id: 2,
      title: "発行者の検証",
      status: "pending",
      description: "検証待機中...",
    },
    {
      id: 3,
      title: "改ざん検知",
      status: "pending",
      description: "検証待機中...",
    },
    {
      id: 4,
      title: "有効期限の確認",
      status: "pending",
      description: "検証待機中...",
    },
  ]);
  const [showDetails, setShowDetails] = useState(false);
  const [currentTime, setCurrentTime] = useState("");

  useEffect(() => {
    // Set current time
    const now = new Date();
    const timeString =
      now.getFullYear() +
      "年" +
      (now.getMonth() + 1) +
      "月" +
      now.getDate() +
      "日 " +
      now.getHours().toString().padStart(2, "0") +
      ":" +
      now.getMinutes().toString().padStart(2, "0") +
      ":" +
      now.getSeconds().toString().padStart(2, "0") +
      " JST";
    setCurrentTime(timeString);

    // Check if URL format is valid
    const validUrl = isValidUrl();
    setIsValidUrlFormat(validUrl);

    if (validUrl) {
      const params = getQueryParams();
      // URL パラメータを使って即座に API を呼び出す
      handleVerification(params);
    } else {
      setVerificationResult("エラー: 無効なURL形式です");
      setVerificationStatus("error");
      setIsVerifying(false);
    }
  }, []);

  const updateVerificationItem = (id, status, description) => {
    setVerificationItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id ? { ...item, status, description } : item
      )
    );
  };

  const simulateVerificationProcess = async () => {
    const steps = [
      {
        id: 1,
        processingText: "デジタル署名を検証中...",
        successText: "証明書に付与されたデジタル署名は有効です",
        delay: 1500,
      },
      {
        id: 2,
        processingText: "発行者の信頼性を検証中...",
        successText: "証明書の発行者は認証されています",
        delay: 2000,
      },
      {
        id: 3,
        processingText: "データの整合性を検証中...",
        successText: "証明書の内容は改ざんされていません",
        delay: 1800,
      },
      {
        id: 4,
        processingText: "有効期限を確認中...",
        successText: "証明書は有効期限内です（2025年度有効）",
        delay: 1200,
      },
    ];

    // Process each verification step
    for (const step of steps) {
      // Start processing this step
      updateVerificationItem(step.id, "processing", step.processingText);

      // Wait for the simulated processing time
      await new Promise((resolve) => setTimeout(resolve, step.delay));
    }
  };

  const handleVerification = async (verificationData) => {
    if (!verificationData.data) {
      setVerificationResult("エラー: データが見つかりません");
      setVerificationStatus("error");
      setIsVerifying(false);
      return;
    }

    setIsVerifying(true);
    setVerificationResult("検証中...");

    try {
      // Start simulated verification process - only shows processing indicators
      await simulateVerificationProcess();

      const response = await fetch(
        `https://api.trust-x.jp/request/verify-vc-information`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(verificationData),
        }
      );

      const result = await response.json();

      if (response.status === 200) {
        // API verification succeeded
        // ここで new 構造にあわせてデータを取得
        setVcData(result.response.verifiedPayload?.vc);
        setResponseData(result.response); // Store the entire response

        // Update all items to success
        updateVerificationItem(
          1,
          "success",
          "証明書に付与されたデジタル署名は有効です"
        );
        updateVerificationItem(
          2,
          "success",
          "証明書の発行者は認証されています"
        );
        updateVerificationItem(
          3,
          "success",
          "証明書の内容は改ざんされていません"
        );
        updateVerificationItem(
          4,
          "success",
          "証明書は有効期限内です（2025年度有効）"
        );

        // Set overall status to success
        setVerificationStatus("success");
        setVerificationResult("検証成功：この証明書は有効です");
      } else {
        // Handle API error
        setVerificationStatus("error");
        setVerificationResult(`エラー: APIリクエストが失敗しました`);

        // Always update all verification items to error when API fails
        updateVerificationItem(1, "error", "検証に失敗しました");
        updateVerificationItem(2, "error", "検証に失敗しました");
        updateVerificationItem(3, "error", "検証に失敗しました");
        updateVerificationItem(4, "error", "検証に失敗しました");
      }
    } catch (error) {
      console.error("検証エラー:", error);
      setVerificationStatus("error");
      setVerificationResult(`エラー: 検証中に問題が発生しました`);

      // Always update all verification items to error when an exception occurs
      updateVerificationItem(1, "error", "検証に失敗しました");
      updateVerificationItem(2, "error", "検証に失敗しました");
      updateVerificationItem(3, "error", "検証に失敗しました");
      updateVerificationItem(4, "error", "検証に失敗しました");
    } finally {
      setIsVerifying(false);
    }
  };

  // Helper to get the icon based on status
  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <i className="fas fa-circle-notch pending"></i>;
      case "processing":
        return <i className="fas fa-circle-notch fa-spin processing"></i>;
      case "success":
        return <i className="fas fa-check-circle success"></i>;
      case "warning":
        return <i className="fas fa-exclamation-circle warning"></i>;
      case "error":
        return <i className="fas fa-times-circle error"></i>;
      default:
        return <i className="fas fa-circle-notch pending"></i>;
    }
  };

  // Helper to get status text and icon for the main verification status
  const getStatusDisplay = () => {
    switch (verificationStatus) {
      case "pending":
      case "processing":
        return (
          <>
            <i className="fas fa-circle-notch fa-spin"></i> 検証中...
          </>
        );
      case "success":
        return (
          <>
            <i className="fas fa-check-circle"></i> {verificationResult}
          </>
        );
      case "warning":
        return (
          <>
            <i className="fas fa-exclamation-circle"></i> {verificationResult}
          </>
        );
      case "error":
        return (
          <>
            <i className="fas fa-times-circle"></i> {verificationResult}
          </>
        );
      default:
        return (
          <>
            <i className="fas fa-circle-notch fa-spin"></i> 検証中...
          </>
        );
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "N/A";

    try {
      // UNIX秒を想定
      const date = new Date(timestamp * 1000);
      return (
        date.getFullYear() +
        "年" +
        (date.getMonth() + 1) +
        "月" +
        date.getDate() +
        "日 " +
        date.getHours().toString().padStart(2, "0") +
        ":" +
        date.getMinutes().toString().padStart(2, "0") +
        ":" +
        date.getSeconds().toString().padStart(2, "0") +
        " JST"
      );
    } catch (e) {
      return timestamp.toString();
    }
  };

  // Format DID for display
  const formatDID = (did) => {
    if (!did) return "N/A";
    if (did.length > 30) {
      return did.substring(0, 15) + "..." + did.substring(did.length - 15);
    }
    return did;
  };

  // Only show content if URL is valid
  if (!isValidUrlFormat) {
    return (
      <div className="container">
        <div className="header">
          <h1>証明書検証結果</h1>
          <p>Verifiable Credential 検証システム</p>
        </div>
        <div className="verification-status error" id="verification-status">
          <i className="fas fa-times-circle"></i> エラー: 無効なURL形式です
        </div>
        <div className="footer">
          <div>DID/VC検証システム v1.0.0</div>
          <div className="timestamp">アクセス時刻: {currentTime}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="header">
        <h1>証明書検証結果</h1>
        <p>Verifiable Credential 検証システム</p>
      </div>

      <div
        className={`verification-status ${verificationStatus}`}
        id="verification-status"
      >
        {getStatusDisplay()}
      </div>

      <div className="content">
        <div className="verification-details">
          <h2>検証結果詳細</h2>

          {verificationItems.map((item) => (
            <div
              key={item.id}
              className={`verification-item ${
                item.status !== "pending" ? "active" : ""
              }`}
            >
              {getStatusIcon(item.status)}
              <div className="verification-item-content">
                <div className="verification-item-title">{item.title}</div>
                <div className="verification-item-description">
                  {item.description}
                </div>
              </div>
            </div>
          ))}
        </div>

        {vcData && (
          <>
            {/* Detailed Verification Information (shown when showDetails is true) */}
            {showDetails && responseData && (
              <div className="verification-details-expanded">
                <h2>検証詳細情報</h2>
                {/* Claims と Disclosures の表示セクション */}
                {responseData.claims && responseData.disclosures && (
                  <div className="detail-section">
                    <h3>資格情報項目</h3>
                    {(() => {
                      // Disclosures は選択的開示のための値の配列です
                      // 各 Disclosure は base64url でエンコードされた JSON 配列で、以下の形式です:
                      // [salt, claim_key, claim_value]
                      // responseData.claims: クレームの定義情報（メタデータ）を含む
                      // - 各クレームのラベル、表示名、データ型などの情報
                      // responseData.disclosures: 実際のクレーム値を含む配列
                      // - 各値は改ざん防止のため、base64urlでエンコードされている
                      // ① Disclosure のデコード結果をまとめる
                      const decodedDisclosures = (
                        responseData.disclosures ?? []
                      )
                        .map((encoded) => {
                          try {
                            const [, claimKey, claimValue] = JSON.parse(
                              base64urlDecode(encoded).toString("utf-8")
                            );
                            return { claimKey, value: claimValue };
                          } catch (e) {
                            console.error("Error decoding disclosure:", e);
                            return null;
                          }
                        })
                        .filter(Boolean);

                      // ② Disclosure を回しながら claims 定義でラベルを決定して描画
                      return decodedDisclosures.map(({ claimKey, value }) => {
                        const claimDef = responseData.claims?.[claimKey];
                        const displayItem =
                          claimDef?.display?.find((d) => d.locale === "ja") ??
                          claimDef?.display?.[0];
                        // FieldLabelMapからのラベル取得を優先し、なければ既存のラベル決定ロジックを使用
                        const label =
                          displayItem?.name ??
                          FieldLabelMap[claimKey] ??
                          claimKey;

                        return (
                          <div className="detail-row" key={claimKey}>
                            <div className="detail-label">{label}</div>
                            <div className="detail-value">
                              {formatCredentialValue(claimKey, value)}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                )}

                <div className="detail-section">
                  <h3>発行者情報</h3>

                  <div className="issuer-logo">
                    <div className="issuer-name">{vcData.issuer?.name}</div>
                  </div>

                  <div className="detail-row">
                    <div className="detail-label">発行者 ID</div>
                    <div className="detail-value">
                      {formatDID(vcData.issuer?.id) || "N/A"}
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <h3>検証情報</h3>

                  <div className="detail-row">
                    <div className="detail-label">署名検証結果</div>
                    <div className="detail-value success">
                      <i className="fas fa-check-circle"></i> 有効
                    </div>
                  </div>

                  {/* メッセージ (あれば) */}
                  <div className="detail-row">
                    <div className="detail-label">検証メッセージ</div>
                    <div className="detail-value">
                      {responseData.message || "N/A"}
                    </div>
                  </div>

                  {/* 発行者ID と 発行者名 */}
                  <div className="detail-row">
                    <div className="detail-label">発行者ID</div>
                    <div className="detail-value">
                      {responseData.verifiedPayload?.issuer?.id
                        ? formatDID(responseData.verifiedPayload.issuer.id)
                        : formatDID(vcData.issuer?.id)}
                    </div>
                  </div>

                  <div className="detail-row">
                    <div className="detail-label">発行者名</div>
                    <div className="detail-value">
                      {responseData.verifiedPayload?.issuer?.name ||
                        vcData.issuer?.name}
                    </div>
                  </div>

                  {/* 有効期限 (exp) と 発行タイムスタンプ(iat) */}
                  {responseData.verifiedPayload?.exp && (
                    <div className="detail-row">
                      <div className="detail-label">有効期限タイムスタンプ</div>
                      <div className="detail-value">
                        {formatTimestamp(responseData.verifiedPayload.exp)}
                      </div>
                    </div>
                  )}

                  {responseData.verifiedPayload?.iat && (
                    <div className="detail-row">
                      <div className="detail-label">発行タイムスタンプ</div>
                      <div className="detail-value">
                        {formatTimestamp(responseData.verifiedPayload.iat)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {verificationStatus === "success" && (
          <div style={{ textAlign: "center", marginTop: "25px" }}>
            <button
              className={`button ${
                verificationStatus === "success"
                  ? "success visible"
                  : "disabled visible"
              }`}
              onClick={() => setShowDetails(!showDetails)}
              disabled={verificationStatus !== "success"}
            >
              {showDetails ? "検証詳細を閉じる" : "検証詳細を表示"}
            </button>
          </div>
        )}
      </div>

      <div className="footer">
        <div>DID/VC検証システム v1.0.0</div>
        <div className="timestamp">検証開始時刻: {currentTime}</div>
      </div>
    </div>
  );
}

export default App;
